import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, ShieldAlert, ShieldQuestion, AlertTriangle, RefreshCw, Loader2, ChevronDown, Share2, Copy, Check, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  verified: { label: 'Verified', color: '#1D6B4F', bg: 'rgba(29,158,117,0.06)', icon: ShieldCheck },
  inaccurate: { label: 'Inaccurate', color: '#D44', bg: 'rgba(212,68,68,0.06)', icon: ShieldAlert },
  needs_review: { label: 'Needs Review', color: '#E5A000', bg: 'rgba(229,160,0,0.06)', icon: AlertTriangle },
  unverifiable: { label: 'Unverifiable', color: '#8C7E6E', bg: 'rgba(153,153,153,0.06)', icon: ShieldQuestion },
};

export default function AccuracyDashboard() {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [filter, setFilter] = useState('all');
  const [copied, setCopied] = useState(false);

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API}/accuracy`, { withCredentials: true });
      setReport(res.data);
    } catch (e) {
      setReport({ has_report: false, claims: [], summary: { total: 0 } });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const runCheck = async () => {
    setChecking(true);
    try {
      const res = await axios.post(`${API}/accuracy/check`, {}, { withCredentials: true });
      setReport(res.data);
    } catch (e) {
      // silently fail
    } finally {
      setChecking(false);
    }
  };

  const claims = report?.claims || [];
  const summary = report?.summary || { total: 0, verified: 0, inaccurate: 0, needs_review: 0, unverifiable: 0 };
  const accuracyScore = report?.accuracy_score;

  const filtered = filter === 'all' ? claims : claims.filter(c => c.status === filter);

  const shareText = accuracyScore != null
    ? `AI is ${accuracyScore}% accurate about my business. ${summary.inaccurate > 0 ? `${summary.inaccurate} claims are WRONG.` : 'All verified!'} Check yours at GetChosenFirst.com`
    : '';

  const handleCopy = () => {
    const text = claims.filter(c => c.status === 'inaccurate').map(c => `"${c.claim}" — ${c.engine} (WRONG: ${c.explanation})`).join('\n');
    navigator.clipboard.writeText(text || shareText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <AppShell active="accuracy">
        <div className="min-h-screen flex items-center justify-center bg-[#F7F4EF]">
          <div className="w-8 h-8 rounded-full animate-spin" style={{ border: '2px solid #D4C9B8', borderTopColor: 'transparent' }} />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell active="accuracy">
      <div className="min-h-screen pb-24 desktop:pb-8 bg-[#F7F4EF]" data-testid="accuracy-dashboard">
        <header className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-1">
            <button onClick={() => navigate('/dashboard')} className="p-1 rounded-none hover:bg-[#EDE8DF]">
              <ArrowLeft size={20} style={{ color: '#8C7E6E' }} />
            </button>
            <h1 className="text-2xl font-medium" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>
              AI Said WHAT?
            </h1>
          </div>
          <p className="text-[13px] ml-9" style={{ color: '#5C5347' }}>See what AI engines say about your business — and what they get wrong.</p>
        </header>

        <main className="px-6 max-w-lg mx-auto tablet:max-w-2xl desktop:max-w-5xl">
          {/* Accuracy Score Hero */}
          {report?.has_report && accuracyScore != null && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
              <div className="p-6 rounded-sm text-center" style={{ backgroundColor: accuracyScore >= 80 ? 'rgba(29,158,117,0.04)' : accuracyScore >= 50 ? 'rgba(229,160,0,0.04)' : 'rgba(212,68,68,0.04)', border: '1px solid #D4C9B8' }} data-testid="accuracy-score-hero">
                <p className="text-[11px] font-medium uppercase tracking-wider mb-2" style={{ color: '#C67A5C' }}>Accuracy Score</p>
                <div className="text-5xl font-medium mb-2" style={{
                  fontFamily: "'DM Sans', sans-serif",
                  color: accuracyScore >= 80 ? '#1D6B4F' : accuracyScore >= 50 ? '#E5A000' : '#D44',
                }}>
                  {accuracyScore}%
                </div>
                <p className="text-[13px]" style={{ color: '#5C5347' }}>
                  {accuracyScore >= 80 ? 'AI is mostly accurate about your business.' :
                   accuracyScore >= 50 ? 'AI gets some things right, but there are issues.' :
                   'AI has significant misinformation about you.'}
                </p>
              </div>
            </motion.div>
          )}

          {/* Summary Stats */}
          {report?.has_report && summary.total > 0 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="grid grid-cols-4 gap-2 mb-4" data-testid="accuracy-summary">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                const count = summary[key] || 0;
                const Icon = cfg.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setFilter(filter === key ? 'all' : key)}
                    className="p-3 rounded-sm text-center transition-all"
                    style={{
                      backgroundColor: filter === key ? cfg.bg : '#F7F4EF',
                      border: filter === key ? `1px solid ${cfg.color}33` : '1px solid #D4C9B8',
                    }}
                    data-testid={`filter-${key}`}
                  >
                    <Icon size={16} className="mx-auto mb-1" style={{ color: cfg.color }} strokeWidth={1.5} />
                    <div className="text-lg font-semibold" style={{ color: cfg.color }}>{count}</div>
                    <div className="text-[10px]" style={{ color: '#5C5347' }}>{cfg.label}</div>
                  </button>
                );
              })}
            </motion.div>
          )}

          {/* Action buttons */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="flex gap-2 mb-4">
            <button
              onClick={runCheck}
              disabled={checking}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-none text-[#F0EBE3] text-sm font-medium transition-opacity hover:opacity-85 disabled:opacity-60"
              style={{ backgroundColor: '#2C2520' }}
              data-testid="run-accuracy-check"
            >
              {checking ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
              {checking ? 'Analyzing claims...' : report?.has_report ? 'Re-check accuracy' : 'Check what AI says about you'}
            </button>
            {summary.inaccurate > 0 && (
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-4 py-3 rounded-none text-sm font-medium transition-colors hover:bg-[#EDE8DF]"
                style={{ border: '1px solid #D4C9B8', color: '#3C3226' }}
                data-testid="copy-inaccuracies"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            )}
          </motion.div>

          {/* Claims list */}
          {filtered.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="space-y-2 mb-4" data-testid="claims-list">
              {filtered.map((claim, i) => (
                <ClaimCard key={i} claim={claim} />
              ))}
            </motion.div>
          )}

          {/* Empty state */}
          {!report?.has_report && !checking && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="py-12 text-center">
              <ShieldQuestion size={48} className="mx-auto mb-4" style={{ color: '#D4C9B8' }} strokeWidth={1} />
              <h3 className="text-lg font-medium mb-2" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>
                What is AI saying about you?
              </h3>
              <p className="text-[13px] mb-6 max-w-sm mx-auto" style={{ color: '#5C5347' }}>
                We'll cross-reference every claim AI engines make about your business with your actual information.
              </p>
            </motion.div>
          )}

          {/* Share CTA */}
          {summary.inaccurate > 0 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <div className="p-5 rounded-sm text-[#F0EBE3] mb-4" style={{ backgroundColor: '#2C2520' }} data-testid="share-accuracy-cta">
                <p className="text-[11px] font-medium uppercase tracking-wider mb-2" style={{ color: '#C67A5C' }}>Spread the word</p>
                <h3 className="text-base font-medium mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  AI is spreading misinformation about you
                </h3>
                <p className="text-[13px] text-[#F0EBE3]/60 mb-4">
                  {summary.inaccurate} claim{summary.inaccurate > 1 ? 's are' : ' is'} wrong. Share this with other pros so they check too.
                </p>
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({ text: shareText, url: window.location.origin });
                    } else {
                      navigator.clipboard.writeText(shareText);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-none text-sm font-medium"
                  style={{ backgroundColor: '#C67A5C' }}
                  data-testid="share-accuracy-btn"
                >
                  <Share2 size={14} /> Share this
                </button>
              </div>
            </motion.div>
          )}
        </main>
      </div>
    </AppShell>
  );
}


function ClaimCard({ claim }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[claim.status] || STATUS_CONFIG.unverifiable;
  const Icon = cfg.icon;

  return (
    <div
      className="p-4 rounded-sm transition-all"
      style={{ backgroundColor: cfg.bg, border: '1px solid #D4C9B8' }}
      data-testid={`claim-${claim.status}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-none flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${cfg.color}15` }}>
          <Icon size={16} style={{ color: cfg.color }} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}>
              {cfg.label}
            </span>
            <span className="text-[10px]" style={{ color: '#8C7E6E' }}>{claim.engine}</span>
            {claim.category && (
              <span className="text-[10px] capitalize" style={{ color: '#8C7E6E' }}>{claim.category}</span>
            )}
          </div>
          <p className="text-[13px] font-medium mb-1" style={{ color: '#3C3226' }}>
            "{claim.claim}"
          </p>
          <button onClick={() => setExpanded(!expanded)} className="text-[11px] flex items-center gap-1 transition-colors" style={{ color: '#5C5347' }}>
            {expanded ? 'Hide' : 'Why?'}
            <ChevronDown size={11} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {expanded && (
              <motion.p
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="text-[12px] mt-1 overflow-hidden"
                style={{ color: '#5C5347' }}
              >
                {claim.explanation}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
